import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import {
  useApiClientsOrNull,
  useMediaQuery,
  MessageAtom,
  ComposerGalleryOrganism,
  RegistryExplorerOrganism,
  PublishImpactDialogOrganism,
  CustomDialog as CustomDialogAtom,
  PrimeReactButton as ButtonAtom,
  InputAtom,
  SegmentedControlAtom,
  type ComposerGalleryEntry,
  type ComposerGalleryKindFilter,
  type RegistryEntry,
  type PublishImpactDependent,
} from 'design-system/my-lib';
import { CATALOG, componentEntryToJsonSchema } from '@center-platform/component-catalog';
import type { CompositionInterfaceDiffResult } from '@center-platform/space-format/conformance';
import type { CompositionA2ui, CompositionExample, CompositionKind } from 'types/composition';
import type { SpaceDocument } from 'types/space';
import type { RootState } from '@center-pwa/store/store';
import { buildCatalogPreviewDocument } from './catalogPreviewDocument';
import './ComposeGalleryPage.scss';

const COMPOSER_AGENT_ENDPOINT = process.env.REACT_APP_COMPOSER_AGENT_ENDPOINT;

type ComposeTab = 'gallery' | 'explorer';

interface CompositionListRow {
  id: string;
  name: string;
  system_name: string;
  description?: string;
  kind: CompositionKind;
  version: number | null;
  canEdit: boolean;
}

interface MetaspaceBasicRow {
  metaspace_id: string;
  name: string;
  description?: string;
  canEdit?: boolean;
}

interface CompositionDetailResponse {
  composition: {
    system_name: string;
    name: string;
    description?: string;
    kind: CompositionKind;
    version: number | null;
    definition: { kind: CompositionKind; interface: { props: Record<string, unknown>; slots: string[]; emits: string[] }; a2ui: CompositionA2ui } | null;
    interfaceSchema: Record<string, unknown> | null;
    examples: CompositionExample[] | null;
    canEdit: boolean;
    verdict: RegistryEntry['verdict'];
  };
}

/** `POST /compositions/:systemName/versions/:v/preflight` response — identical shape to
 * `publishCompositionVersion`'s 409 `impact` body (both wrap `preflightCompositionPublish`). */
interface PublishPreflightResponse {
  diff: CompositionInterfaceDiffResult;
  dependents: PublishImpactDependent[];
  hiddenDependents: boolean;
  hiddenFailingDependents: boolean;
  wouldBlock: boolean;
}

interface PublishCompositionResponse {
  composition: CompositionDetailResponse['composition'];
  impact: PublishPreflightResponse;
}

function blankSpaceDocument(): SpaceDocument {
  return {
    formatVersion: 'space/v1',
    catalogVersion: 1,
    surface: { family: 'SpaceGrid', componentId: 'root', layout: 'uniform' },
    a2ui: { version: 'v1.0', catalogId: 'center-platform', root: 'root', components: [{ id: 'root', component: 'SpaceGrid', children: [] }] },
  };
}

function slugify(name: string): string {
  return name.trim().toLowerCase().replaceAll(/[^a-z0-9]+/g, '_').replaceAll(/^_+|_+$/g, '') || 'untitled';
}

/** Wraps a composition's own A2UI subtree as a renderable Space document — used for both the
 * gallery card's live preview and seeding a "New from template" composition's initial content. */
function compositionToSpaceDocument(kind: CompositionKind, a2ui: CompositionA2ui): SpaceDocument {
  return {
    formatVersion: 'space/v1',
    catalogVersion: 1,
    surface: { family: kind, componentId: a2ui.root, layout: 'uniform' },
    a2ui: { version: 'v1.0', catalogId: 'center-platform', components: a2ui.components, root: a2ui.root },
  };
}

/**
 * `/compose` — the gallery (SC1) and Explorer (SC6) tabs. Gallery lists Spaces + compositions the
 * caller can `view` (server-filtered by `GET /metaspaces`/`GET /compositions`, never re-filtered
 * here); Explorer browses the merged code-backed + composition-backed registry with generated docs,
 * knobs, and save-as-story. There is no composition-target composer shell yet (`ComposeTargetPage`
 * only handles Spaces) — opening a composition lands in the Explorer tab with it preselected rather
 * than a dead link; see `.ralph/IMPLEMENTATION_PLAN.md` for the follow-up.
 */
const ComposeGalleryPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const apiClients = useApiClientsOrNull();
  const organizationId = useSelector((state: RootState) => state.userProfile?.userSphere?.properties?.sphereId);
  const [searchParams] = useSearchParams();
  const openEntryParam = searchParams.get('openEntry');
  const belowDesktop = useMediaQuery('(max-width: 1023px)');

  const [tab, setTab] = useState<ComposeTab>(openEntryParam ? 'explorer' : 'gallery');

  const [galleryEntries, setGalleryEntries] = useState<ComposerGalleryEntry[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [galleryError, setGalleryError] = useState<string | undefined>();
  const [kindFilter, setKindFilter] = useState<ComposerGalleryKindFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [registryEntries, setRegistryEntries] = useState<RegistryEntry[]>([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [savingStory, setSavingStory] = useState(false);

  // Preflight → dialog → publish (Phase 5.3): `publishEntry` names which entry the dialog covers;
  // `publishPreflight` is either a fresh preflight read or, after a 409, the publish route's own
  // `impact` body — same shape, so the dialog re-renders from whichever is freshest without a
  // separate "blocked" state.
  const [publishEntry, setPublishEntry] = useState<RegistryEntry | null>(null);
  const [publishPreflight, setPublishPreflight] = useState<PublishPreflightResponse | null>(null);
  const [preflighting, setPreflighting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Preflight and publish failures that reached no gate verdict — kept separate from
  // `publishPreflight` (which only ever holds a real, route-computed impact) so a transport
  // failure can never be mistaken for an allow/block decision.
  const [preflightError, setPreflightError] = useState<string | undefined>();
  const [publishError, setPublishError] = useState<string | undefined>();

  const [newTarget, setNewTarget] = useState<{
    kind: 'space' | CompositionKind;
    focusAgent?: boolean;
    fromTemplate?: ComposerGalleryEntry;
  } | null>(null);
  const [newName, setNewName] = useState('');
  const [newError, setNewError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const [previewDocuments, setPreviewDocuments] = useState<Record<string, SpaceDocument | null>>({});

  // Code-backed `CATALOG` entries (EAINT-844 Phase F2, owner directive) — the gallery lists them
  // alongside user-authored Spaces/compositions, view-only, grouped/faceted by role via the
  // "Built-in" chip. `CATALOG` is a static module-level constant, so this needs no fetch/effect;
  // the synthetic single-node preview mirrors `ComposeCompositionPage`'s locked-view document.
  // Deliberately excluded from `galleryEntries` (and therefore from "New from template" — they
  // cannot be cloned) and from the API-driven load effects above.
  const builtInEntries = useMemo<ComposerGalleryEntry[]>(
    () =>
      CATALOG.filter((entry) => entry.surfaces.includes('space') && !entry.internal).map((entry) => ({
        id: entry.type,
        targetKind: 'catalog',
        kind: entry.role,
        name: entry.type,
        // `ComponentEntry.description` is English-only developer/agent prompt prose, not authored
        // UI copy — rendering it on a card would ship untranslated implementation text to every
        // locale. Built-ins get localized copy instead: per-component-type where it has been
        // authored, falling back to generic per-role copy so a newly added catalog entry still
        // renders something sensible before its own string lands.
        description: t([
          `composer.gallery.builtInDescription.type.${entry.type}`,
          `composer.gallery.builtInDescription.${entry.role}`,
        ]),
        canEdit: false,
        builtIn: true,
        previewDocument: buildCatalogPreviewDocument(entry.type),
      })),
    [t]
  );

  const loadGallery = useCallback(async () => {
    if (!apiClients?.schemaClient || belowDesktop) return;
    setGalleryLoading(true);
    setGalleryError(undefined);
    try {
      const [compositionsRes, metaspacesRes] = await Promise.all([
        apiClients.schemaClient.get<{ compositions: CompositionListRow[] }>('/compositions'),
        apiClients.schemaClient
          .get<{ metaspaces: MetaspaceBasicRow[] }>('/metaspaces?basic=true&includeCanEdit=true')
          .catch((err: unknown) => {
            // `/metaspaces` 404s (by design) when the filtered result set is empty.
            if ((err as { response?: { status?: number } })?.response?.status === 404) return { metaspaces: [] };
            throw err;
          }),
      ]);

      const spaceEntries: ComposerGalleryEntry[] = metaspacesRes.metaspaces.map((m) => ({
        id: m.metaspace_id,
        targetKind: 'space',
        kind: 'space',
        name: m.name,
        description: m.description,
        canEdit: m.canEdit ?? false,
      }));
      const compositionEntries: ComposerGalleryEntry[] = compositionsRes.compositions.map((c) => ({
        id: c.system_name,
        targetKind: 'composition',
        kind: c.kind,
        name: c.name,
        description: c.description,
        canEdit: c.canEdit,
      }));
      setGalleryEntries([...spaceEntries, ...compositionEntries]);
    } catch {
      setGalleryError(t('composer.gallery.loadFailed'));
    } finally {
      setGalleryLoading(false);
    }
  }, [apiClients?.schemaClient, belowDesktop, t]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  // Populates each card's live mini-render (SC1) lazily, one detail fetch per entry — the list
  // routes intentionally carry no definition/a2ui payload (kept lean for the common list-only
  // view). `requestedPreviews` (a ref, written synchronously before the await) is the dedupe
  // key rather than `previewDocuments`: state only lands after the request settles, so a
  // state-based guard re-requests every still-in-flight entry on each intermediate render.
  const requestedPreviews = useRef(new Set<string>());
  const loadPreview = useCallback(
    async (entry: ComposerGalleryEntry) => {
      if (!apiClients?.schemaClient || belowDesktop) return;
      try {
        if (entry.targetKind === 'space') {
          const response = await apiClients.schemaClient.get<{ definition: SpaceDocument | null }>(
            `/metaspaces/${encodeURIComponent(entry.id)}`
          );
          setPreviewDocuments((prev) => ({ ...prev, [entry.id]: response.definition ?? null }));
        } else {
          const { composition } = await apiClients.schemaClient.get<CompositionDetailResponse>(
            `/compositions/${encodeURIComponent(entry.id)}`
          );
          const a2ui = composition.definition?.a2ui;
          setPreviewDocuments((prev) => ({
            ...prev,
            [entry.id]: a2ui ? compositionToSpaceDocument(composition.kind, a2ui) : null,
          }));
        }
      } catch {
        setPreviewDocuments((prev) => ({ ...prev, [entry.id]: null }));
      }
    },
    [apiClients?.schemaClient, belowDesktop]
  );

  useEffect(() => {
    galleryEntries.forEach((entry) => {
      if (requestedPreviews.current.has(entry.id)) return;
      requestedPreviews.current.add(entry.id);
      loadPreview(entry);
    });
  }, [galleryEntries, loadPreview]);

  const loadRegistry = useCallback(async () => {
    if (!apiClients?.schemaClient || belowDesktop) return;
    setRegistryLoading(true);
    try {
      const catalogEntries: RegistryEntry[] = CATALOG.filter(
        (entry) => entry.surfaces.includes('space') && !entry.internal
      ).map((entry) => ({
        id: entry.type,
        origin: 'catalog',
        // `role` (EAINT-844 Phase F1), not `category` — the Explorer tree groups on `kind`, and
        // `role` is what makes a catalog row's group merge with a composition row of the same
        // kind (both now speak the same surface/section/primitive vocabulary).
        kind: entry.role,
        name: entry.type,
        description: entry.description,
        interfaceSchema: componentEntryToJsonSchema(entry),
      }));
      const { compositions } = await apiClients.schemaClient.get<{ compositions: CompositionListRow[] }>('/compositions');
      const compositionEntries: RegistryEntry[] = compositions.map((c) => ({
        id: c.system_name,
        origin: 'composition',
        kind: c.kind,
        name: c.name,
        description: c.description,
        canEdit: c.canEdit,
        version: c.version,
      }));
      setRegistryEntries([...catalogEntries, ...compositionEntries]);
    } catch {
      setRegistryEntries([]);
    } finally {
      setRegistryLoading(false);
    }
  }, [apiClients?.schemaClient, belowDesktop]);

  useEffect(() => {
    if (tab === 'explorer' && registryEntries.length === 0 && !registryLoading) {
      loadRegistry();
    }
  }, [tab, registryEntries.length, registryLoading, loadRegistry]);

  const handleSelectRegistryEntry = useCallback(
    async (id: string) => {
      setSelectedEntryId(id);
      const existing = registryEntries.find((e) => e.id === id);
      if (!existing || existing.origin !== 'composition' || existing.interfaceSchema !== undefined || !apiClients?.schemaClient) {
        return;
      }
      try {
        const { composition } = await apiClients.schemaClient.get<CompositionDetailResponse>(
          `/compositions/${encodeURIComponent(id)}`
        );
        setRegistryEntries((prev) =>
          prev.map((e) =>
            e.id === id
              ? {
                  ...e,
                  description: composition.description ?? e.description,
                  interfaceSchema: composition.interfaceSchema,
                  emits: composition.definition?.interface?.emits ?? [],
                  examples: composition.examples,
                  canEdit: composition.canEdit,
                  version: composition.version,
                  verdict: composition.verdict ?? null,
                }
              : e
          )
        );
      } catch {
        // Leave the entry as-is — docs/knobs render empty rather than blocking selection.
      }
    },
    [registryEntries, apiClients?.schemaClient]
  );

  // `/compose/composition/:name` (EAINT-844 Phase F1, Task 3) — a catalog-origin entry opens
  // locked (read-only, no Save/Publish/Fork); a composition-origin entry lands there too, since
  // the page itself is what decides whether a real composer canvas exists for it yet (OQ-8).
  const handleOpenRegistryEntryInComposer = useCallback(
    (entry: RegistryEntry) => navigate(`/studio/composition/${encodeURIComponent(entry.id)}`),
    [navigate]
  );

  // `?openEntry=<systemName>` (the composer inspector's "Open in playground" deep link) preselects
  // the Explorer entry once the registry has loaded — mirrors `handleOpenGalleryEntry`'s in-page
  // composition-open path, just entered from a different route instead of a click.
  useEffect(() => {
    if (openEntryParam && selectedEntryId !== openEntryParam && registryEntries.some((e) => e.id === openEntryParam)) {
      handleSelectRegistryEntry(openEntryParam);
    }
  }, [openEntryParam, selectedEntryId, registryEntries, handleSelectRegistryEntry]);

  const handleSaveStory = useCallback(
    async (entry: RegistryEntry, story: { name: string; description?: string; props: Record<string, unknown> }) => {
      if (entry.origin !== 'composition' || !apiClients?.schemaClient) return;
      setSavingStory(true);
      try {
        const { composition } = await apiClients.schemaClient.get<CompositionDetailResponse>(
          `/compositions/${encodeURIComponent(entry.id)}`
        );
        if (!composition.version || !composition.definition) return;
        const examples = [...(composition.examples ?? []), story as CompositionExample];
        await apiClients.schemaClient.put(`/compositions/${encodeURIComponent(entry.id)}/versions/${composition.version}`, {
          kind: composition.definition.kind,
          interface: composition.definition.interface,
          a2ui: composition.definition.a2ui,
          examples,
        });
        logAnalyticsEvent('explorer_story_saved', undefined, { kind: composition.kind });
        setRegistryEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, examples } : e)));
      } catch {
        // Save failures surface as the story simply not appearing in the saved list — the
        // knob values the caller entered stay on-screen (uncontrolled by this handler) so
        // nothing is lost; a dedicated error toast is a follow-up (no toast primitive wired
        // into this page yet).
      } finally {
        setSavingStory(false);
      }
    },
    [apiClients?.schemaClient, logAnalyticsEvent]
  );

  // Preflight → dialog → publish (Phase 5.3, SC6). No client-side authz and no client-side
  // gating decision: `wouldBlock` and every dependent status come straight from the route
  // response and are rendered as-is by `PublishImpactDialogOrganism` — a failed fetch leaves the
  // dialog closed (the restrictive state) rather than opening it with a guessed default.
  const handleRequestPublish = useCallback(
    async (entry: RegistryEntry) => {
      if (entry.origin !== 'composition' || typeof entry.version !== 'number' || !apiClients?.schemaClient) return;
      setPreflighting(true);
      setPreflightError(undefined);
      try {
        const preflight = await apiClients.schemaClient.post<PublishPreflightResponse>(
          `/compositions/${encodeURIComponent(entry.id)}/versions/${entry.version}/preflight`
        );
        setPublishEntry(entry);
        setPublishPreflight(preflight);
      } catch {
        // The dialog stays closed — no impact was computed, so there is nothing truthful to
        // render in it — but the caller is told the request failed rather than seeing the click
        // do nothing. Publish stays reachable and the caller can retry.
        setPreflightError(t('composer.publishImpact.preflightFailed'));
      } finally {
        setPreflighting(false);
      }
    },
    [apiClients?.schemaClient, t]
  );

  const handleHidePublishDialog = useCallback(() => {
    setPublishEntry(null);
    setPublishPreflight(null);
    setPublishError(undefined);
  }, []);

  const handleConfirmPublish = useCallback(async () => {
    if (!publishEntry || typeof publishEntry.version !== 'number' || !apiClients?.schemaClient) return;
    setPublishing(true);
    setPublishError(undefined);
    try {
      const { composition } = await apiClients.schemaClient.post<PublishCompositionResponse>(
        `/compositions/${encodeURIComponent(publishEntry.id)}/versions/${publishEntry.version}/publish`
      );
      setRegistryEntries((prev) =>
        prev.map((e) =>
          e.id === publishEntry.id ? { ...e, version: composition.version, verdict: composition.verdict ?? null } : e
        )
      );
      handleHidePublishDialog();
    } catch (err: unknown) {
      // A 409 carries the same `impact` shape as the preflight read — surface it in the same
      // dialog instead of a generic error so the caller sees exactly why it's blocked.
      const body = (err as { response?: { status?: number; data?: { impact?: PublishPreflightResponse } } })?.response;
      if (body?.status === 409 && body.data?.impact) {
        setPublishPreflight(body.data.impact);
        return;
      }
      // Anything else (transport, 5xx, a 409 with no impact body) reached no gate verdict —
      // keep the dialog open with an error instead of letting Publish appear to do nothing.
      setPublishError(t('composer.publishImpact.publishFailed'));
    } finally {
      setPublishing(false);
    }
  }, [publishEntry, apiClients?.schemaClient, handleHidePublishDialog, t]);

  // Kind-facet filtering moved into ComposerGalleryOrganism (EAINT-844 Phase F7a) — sectioning
  // needs the full, unfiltered-by-kind entry set to decide which sections are non-empty. This
  // stage only applies the search term.
  const filteredGalleryEntries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const withPreviews = galleryEntries.map((entry) => ({ ...entry, previewDocument: previewDocuments[entry.id] }));
    return [...withPreviews, ...builtInEntries].filter((entry) => {
      if (!term) return true;
      return entry.name.toLowerCase().includes(term) || (entry.description ?? '').toLowerCase().includes(term);
    });
  }, [galleryEntries, builtInEntries, searchTerm, previewDocuments]);

  const handleOpenGalleryEntry = useCallback(
    (entry: ComposerGalleryEntry) => {
      // Navigating branches deliberately do NOT emit `composer_opened` — the destination route
      // (`ComposeTargetPage` / `ComposeCompositionPage`) owns that event, so emitting here too
      // would double-count every open and inflate built-in/Space open-rate against the
      // in-page Explorer branch below.
      if (entry.targetKind === 'space') {
        navigate(`/studio/space/${encodeURIComponent(entry.id)}`);
        return;
      }
      if (entry.targetKind === 'catalog') {
        navigate(`/studio/composition/${encodeURIComponent(entry.id)}`);
        return;
      }
      logAnalyticsEvent('composer_opened', undefined, {
        targetKind: entry.targetKind,
        scopeKind: 'org',
      });
      setTab('explorer');
      setSelectedEntryId(entry.id);
    },
    [navigate, logAnalyticsEvent]
  );

  const openNewDialog = useCallback((kind: 'space' | CompositionKind, focusAgent = false) => {
    setNewTarget({ kind, focusAgent });
    setNewName('');
    setNewError(null);
  }, []);

  const openTemplatePicker = useCallback(() => {
    setTemplatePickerOpen(true);
  }, []);

  const handlePickTemplate = useCallback(
    (entry: ComposerGalleryEntry) => {
      // Code-backed catalog entries have no stored definition to clone, and their `kind` carries
      // a `ComponentRole` (e.g. `'primitive'`) rather than a `CompositionKind`. Enforce the
      // exclusion here rather than relying on the picker's candidate list staying free of them —
      // otherwise a future change that points the picker at `filteredGalleryEntries` would POST an
      // invalid `kind`. Every entry that reaches this point is a real, non-built-in composition —
      // `entry.kind` is `CompositionListRow.kind: CompositionKind` at construction (`loadGallery`
      // above), never widened by anything this guard doesn't already exclude — so the cast below
      // reflects a value conformance now enforces at write time, not an assumption.
      if (entry.builtIn || entry.targetKind === 'catalog') return;
      setTemplatePickerOpen(false);
      setNewTarget({
        kind: entry.targetKind === 'space' ? 'space' : (entry.kind as CompositionKind),
        fromTemplate: entry,
      });
      setNewName(t('composer.gallery.newFromTemplateNameSeed', { name: entry.name }));
      setNewError(null);
    },
    [t]
  );

  const handleCreate = useCallback(async () => {
    if (!newTarget || !apiClients?.schemaClient || !newName.trim()) return;
    setCreating(true);
    setNewError(null);
    try {
      let sourceSpaceDocument: SpaceDocument | undefined;
      let sourceComposition: CompositionDetailResponse['composition'] | undefined;
      if (newTarget.fromTemplate) {
        if (newTarget.fromTemplate.targetKind === 'space') {
          const response = await apiClients.schemaClient.get<{ definition: SpaceDocument | null }>(
            `/metaspaces/${encodeURIComponent(newTarget.fromTemplate.id)}`
          );
          sourceSpaceDocument = response.definition ?? undefined;
        } else {
          const { composition } = await apiClients.schemaClient.get<CompositionDetailResponse>(
            `/compositions/${encodeURIComponent(newTarget.fromTemplate.id)}`
          );
          sourceComposition = composition;
        }
      }

      if (newTarget.kind === 'space') {
        if (!organizationId) throw new Error('missing organizationId');
        const result = await apiClients.schemaClient.post<{ metaspace: { metaspace_id: string } }>('/metaspaces', {
          name: newName.trim(),
          scope: 'tenant',
          definition: sourceSpaceDocument ?? blankSpaceDocument(),
        });
        setNewTarget(null);
        navigate(`/studio/space/${encodeURIComponent(result.metaspace.metaspace_id)}${newTarget.focusAgent ? '?agent=1' : ''}`);
      } else {
        const systemName = slugify(newName);
        await apiClients.schemaClient.post('/compositions', {
          name: newName.trim(),
          systemName,
          kind: newTarget.kind,
          interface: sourceComposition?.definition?.interface ?? { props: {}, slots: [], emits: [] },
          a2ui: sourceComposition?.definition?.a2ui ?? { components: [{ id: 'root', component: 'Section' }], root: 'root' },
        });
        setNewTarget(null);
        await loadGallery();
        setTab('explorer');
        setSelectedEntryId(systemName);
        await loadRegistry();
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setNewError(status === 409 ? t('composer.gallery.newNameTaken') : t('composer.gallery.newFailed'));
    } finally {
      setCreating(false);
    }
  }, [newTarget, apiClients?.schemaClient, newName, organizationId, navigate, loadGallery, loadRegistry, t]);

  if (belowDesktop) {
    return (
      <div className="compose-gallery-page compose-gallery-page--desktop-only">
        <MessageAtom severity="info" text={t('composer.page.desktopOnlyTitle')} />
        <p className="compose-gallery-page__desktop-only-description">{t('composer.page.desktopOnlyDescription')}</p>
      </div>
    );
  }

  return (
    <div className="compose-gallery-page">
      <div className="compose-gallery-page__tabs">
        <SegmentedControlAtom
          options={[
            { value: 'gallery', label: t('composer.gallery.tabLabel') },
            { value: 'explorer', label: t('composer.explorer.tabLabel') },
          ]}
          value={tab}
          onChange={(value) => setTab(value as ComposeTab)}
          mode="single"
          variant="pills"
          size="small"
          ariaLabel={t('composer.gallery.tabsLabel')}
        />
      </div>

      <div className="compose-gallery-page__body">
        {tab === 'gallery' ? (
          <ComposerGalleryOrganism
            entries={filteredGalleryEntries}
            loading={galleryLoading}
            errorMessage={galleryError}
            kindFilter={kindFilter}
            onKindFilterChange={setKindFilter}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            onOpen={handleOpenGalleryEntry}
            onNewBlankSpace={() => openNewDialog('space')}
            onNewBlankComposition={(kind) => openNewDialog(kind)}
            onNewFromTemplate={openTemplatePicker}
            onNewWithAgent={COMPOSER_AGENT_ENDPOINT ? () => openNewDialog('space', true) : undefined}
            apiClient={apiClients?.schemaClient}
          />
        ) : (
          <RegistryExplorerOrganism
            entries={registryEntries}
            loading={registryLoading}
            selectedId={selectedEntryId}
            onSelect={handleSelectRegistryEntry}
            onOpenInComposer={handleOpenRegistryEntryInComposer}
            onSaveStory={handleSaveStory}
            savingStory={savingStory}
            onPublishRequest={handleRequestPublish}
            publishRequestPending={preflighting}
            publishRequestError={preflightError}
            apiClient={apiClients?.schemaClient}
          />
        )}
      </div>

      {publishEntry && publishPreflight && (
        <PublishImpactDialogOrganism
          visible
          onHide={handleHidePublishDialog}
          breaking={publishPreflight.diff.breaking}
          additive={publishPreflight.diff.additive}
          dependents={publishPreflight.dependents}
          hiddenDependents={publishPreflight.hiddenDependents}
          hiddenFailingDependents={publishPreflight.hiddenFailingDependents}
          wouldBlock={publishPreflight.wouldBlock}
          onPublish={handleConfirmPublish}
          publishing={publishing}
          errorMessage={publishError}
          data-testid="compose-gallery-publish-impact-dialog"
        />
      )}

      {templatePickerOpen && (
        <CustomDialogAtom
          header={t('composer.gallery.templatePicker.header')}
          visible
          onHide={() => setTemplatePickerOpen(false)}
          footer={
            <ButtonAtom variant="text" size="small" onClick={() => setTemplatePickerOpen(false)}>
              {t('composer.gallery.newCancel')}
            </ButtonAtom>
          }
        >
          {galleryEntries.length === 0 ? (
            <p>{t('composer.gallery.templatePicker.empty')}</p>
          ) : (
            <ul className="compose-gallery-page__template-picker-list">
              {galleryEntries.map((entry) => (
                <li key={`${entry.targetKind}:${entry.id}`}>
                  <ButtonAtom
                    variant="text"
                    size="small"
                    onClick={() => handlePickTemplate(entry)}
                    data-testid={`composer-gallery-template-option-${entry.id}`}
                  >
                    {entry.name}
                  </ButtonAtom>
                </li>
              ))}
            </ul>
          )}
        </CustomDialogAtom>
      )}

      {newTarget && (
        <CustomDialogAtom
          header={t(newTarget.kind === 'space' ? 'composer.gallery.new.space' : 'composer.gallery.newCompositionHeader')}
          visible
          onHide={() => setNewTarget(null)}
          footer={
            <>
              <ButtonAtom variant="text" size="small" onClick={() => setNewTarget(null)}>
                {t('composer.gallery.newCancel')}
              </ButtonAtom>
              <ButtonAtom
                variant="primary"
                size="small"
                disabled={!newName.trim() || creating}
                loading={creating}
                onClick={handleCreate}
              >
                {t('composer.gallery.newCreate')}
              </ButtonAtom>
            </>
          }
        >
          {newError && <MessageAtom severity="error" text={newError} />}
          <InputAtom
            value={newName}
            placeholder={t('composer.gallery.newNamePlaceholder')}
            onChange={(e) => setNewName(e.target.value)}
            aria-label={t('composer.gallery.newNamePlaceholder')}
          />
        </CustomDialogAtom>
      )}
    </div>
  );
};

export default ComposeGalleryPage;
